import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertOctagon,
  CheckCircle2,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { returnsService } from '@/services/returns';
import {
  securitySealsService,
  type SecuritySealWithDispatch,
} from '@/services/securitySeals';
import type { ReturnOrder } from '@/types/returns';
import { ApiError } from '@/lib/errors';
import { BarcodeScanner } from './BarcodeScanner';
import { haptics } from './haptics';

/**
 * Return-intake scan flow — native layout.
 *
 *   Header (return number + customer)
 *   Scanner zone (top) OR per-tag inspection card when one is staged
 *   Scrolling list of seals scanned so far
 *   Sticky bottom action bar with "Move to RECEIVED" / "Reject return"
 */
export function PwaReturnScanPage() {
  const { returnId } = useParams<{ returnId: string }>();
  const navigate = useNavigate();
  const [ret, setRet] = useState<ReturnOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingSeal, setPendingSeal] = useState<SecuritySealWithDispatch | null>(null);
  const [brokenReason, setBrokenReason] = useState('');
  const [seals, setSeals] = useState<
    Array<SecuritySealWithDispatch & { decision: 'INTACT' | 'BROKEN' }>
  >([]);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    if (!returnId) return;
    setError(null);
    try {
      const r = await returnsService.get(returnId);
      setRet(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load return');
    }
  }, [returnId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onScan = useCallback(
    async (raw: string) => {
      const code = raw.trim().toUpperCase();
      if (!code || busy) return;
      if (seals.some((s) => s.code === code)) {
        haptics.warn();
        toast.error(`Seal ${code} already recorded`);
        return;
      }
      setBusy(true);
      try {
        const seal = await securitySealsService.lookupByCode(code);
        haptics.tap();
        setPendingSeal(seal);
        setBrokenReason('');
      } catch (err) {
        haptics.warn();
        toast.error(err instanceof ApiError ? err.message : 'Seal not found');
      } finally {
        setBusy(false);
      }
    },
    [busy, seals],
  );

  const confirmDecision = useCallback(
    async (intact: boolean) => {
      if (!pendingSeal || !returnId || busy) return;
      if (!intact && !brokenReason.trim()) {
        haptics.warn();
        toast.error('Reason is required when marking a seal broken.');
        return;
      }
      setBusy(true);
      try {
        await securitySealsService.recordReceive(pendingSeal.code, {
          returnOrderId: returnId,
          intact,
          brokenReason: intact ? undefined : brokenReason.trim(),
        });
        setSeals((prev) => [
          ...prev,
          { ...pendingSeal, decision: intact ? 'INTACT' : 'BROKEN' },
        ]);
        setPendingSeal(null);
        setBrokenReason('');
        intact ? haptics.success() : haptics.warn();
        toast.success(intact ? 'Seal recorded as intact' : 'Seal recorded as broken');
      } catch (err) {
        haptics.warn();
        toast.error(err instanceof ApiError ? err.message : 'Could not record seal');
      } finally {
        setBusy(false);
      }
    },
    [pendingSeal, returnId, brokenReason, busy],
  );

  const moveToReceived = useCallback(async () => {
    if (!returnId || busy) return;
    setBusy(true);
    try {
      await returnsService.updateStatus(returnId, { status: 'RECEIVED' });
      haptics.success();
      toast.success('Return moved to RECEIVED');
      navigate('/pwa/returns');
    } catch (err) {
      haptics.warn();
      toast.error(err instanceof ApiError ? err.message : 'Could not move return');
    } finally {
      setBusy(false);
    }
  }, [returnId, busy, navigate]);

  const rejectReturn = useCallback(async () => {
    if (!returnId || busy) return;
    if (!rejectReason.trim()) {
      haptics.warn();
      toast.error('A rejection reason is required.');
      return;
    }
    setBusy(true);
    try {
      await returnsService.updateStatus(returnId, {
        status: 'REJECTED',
        reason: rejectReason.trim(),
      });
      haptics.tap();
      toast.success('Return rejected');
      navigate('/pwa/returns');
    } catch (err) {
      haptics.warn();
      toast.error(err instanceof ApiError ? err.message : 'Could not reject');
    } finally {
      setBusy(false);
    }
  }, [returnId, rejectReason, busy, navigate]);

  if (!returnId) {
    return (
      <div className="px-4 py-8 text-center text-[13px] text-muted-foreground">
        No return id in the URL.
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-4 pt-4">
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-[13px] text-destructive">
          {error}
        </div>
      </div>
    );
  }
  if (!ret) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const anyBroken = seals.some((s) => s.decision === 'BROKEN');

  return (
    <div className="pwa-anim-in flex flex-col gap-4 pb-[260px] pt-3">
      <div className="px-4">
        <p className="text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
          Receive
        </p>
        <h1 className="font-mono text-[24px] font-bold leading-tight tracking-tight text-foreground">
          {ret.returnNumber}
        </h1>
        <p className="mt-1 text-[12.5px] text-muted-foreground">
          {ret.customer?.name} · Order {ret.salesOrder?.orderNumber}
        </p>
      </div>

      {!pendingSeal && (
        <div className="px-4">
          <div className="rounded-2xl border border-accent/30 bg-accent/5 p-3">
            <BarcodeScanner
              label="Scan a security seal"
              placeholder="Scan the tag's code"
              onSubmit={onScan}
              disabled={busy}
            />
          </div>
        </div>
      )}

      {pendingSeal && (
        <div className="pwa-anim-in px-4">
          <div className="space-y-3 rounded-2xl border border-primary bg-primary/5 p-4 shadow-sm">
            <div>
              <div className="font-mono text-[14px] font-semibold text-foreground">
                {pendingSeal.code}
              </div>
              <div className="mt-0.5 text-[12px] text-muted-foreground">
                SKU{' '}
                <span className="font-mono text-foreground">
                  {pendingSeal.dispatchOrderItem.sku}
                </span>{' '}
                · {pendingSeal.dispatchOrderItem.name}
              </div>
            </div>
            <p className="text-[12.5px] font-medium text-foreground">
              Inspect the tag — is it intact?
            </p>
            <input
              value={brokenReason}
              onChange={(e) => setBrokenReason(e.target.value)}
              disabled={busy}
              placeholder="If broken, describe the damage (required for broken)"
              className="block h-12 w-full rounded-2xl border border-border bg-card px-3.5 text-[13px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => confirmDecision(true)}
                disabled={busy}
                className="pwa-tap inline-flex h-12 items-center justify-center gap-1.5 rounded-2xl bg-success text-[14px] font-semibold text-success-foreground shadow-sm active:opacity-90 disabled:opacity-40"
              >
                <ShieldCheck className="h-4 w-4" />
                Intact
              </button>
              <button
                type="button"
                onClick={() => confirmDecision(false)}
                disabled={busy || !brokenReason.trim()}
                className="pwa-tap inline-flex h-12 items-center justify-center gap-1.5 rounded-2xl bg-destructive text-[14px] font-semibold text-destructive-foreground shadow-sm active:opacity-90 disabled:opacity-40"
              >
                <ShieldAlert className="h-4 w-4" />
                Broken
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                setPendingSeal(null);
                setBrokenReason('');
              }}
              disabled={busy}
              className="pwa-tap w-full rounded-xl px-3 py-2 text-[12.5px] text-muted-foreground active:bg-muted"
            >
              Cancel — re-scan
            </button>
          </div>
        </div>
      )}

      {seals.length > 0 && (
        <div className="px-4">
          <div className="mb-2 text-[11.5px] font-medium uppercase tracking-wide text-muted-foreground">
            Scanned ({seals.length})
          </div>
          <ul className="space-y-1.5">
            {seals.map((s) => (
              <li
                key={s.id}
                className={`flex items-center gap-2.5 rounded-2xl border px-3.5 py-2.5 ${
                  s.decision === 'INTACT'
                    ? 'border-success/30 bg-success/5'
                    : 'border-destructive/40 bg-destructive/5'
                }`}
              >
                {s.decision === 'INTACT' ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
                ) : (
                  <XCircle className="h-5 w-5 shrink-0 text-destructive" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[13px] font-semibold text-foreground">
                    {s.code}
                  </div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {s.dispatchOrderItem.sku}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {anyBroken && (
        <div className="px-4">
          <div className="flex items-start gap-2 rounded-2xl border border-destructive/30 bg-destructive/5 p-3 text-[12px] text-destructive">
            <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              One or more seals are broken. This return cannot be moved to
              RECEIVED — reject it (or void the broken seal on the desktop
              before continuing).
            </span>
          </div>
        </div>
      )}

      {/* ─── Sticky bottom action bar (sits ABOVE the tab bar) ───── */}
      <div
        className="pwa-pl-safe pwa-pr-safe fixed inset-x-0 z-20 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/85"
        style={{
          bottom:
            'calc(var(--pwa-tab-bar-h, 64px) + env(safe-area-inset-bottom))',
        }}
      >
        <div className="space-y-2 px-4 pb-3 pt-3">
          <button
            type="button"
            onClick={moveToReceived}
            disabled={busy || seals.length === 0 || anyBroken}
            className="pwa-tap inline-flex h-12 w-full items-center justify-center rounded-2xl bg-primary text-[14px] font-semibold text-primary-foreground shadow-sm active:opacity-90 disabled:opacity-40"
          >
            Move to RECEIVED
          </button>
          {!rejectOpen ? (
            <button
              type="button"
              onClick={() => setRejectOpen(true)}
              className="pwa-tap w-full rounded-xl px-3 py-2 text-[12.5px] text-destructive active:bg-destructive/10"
            >
              Reject this return
            </button>
          ) : (
            <div className="space-y-2 rounded-2xl border border-destructive/30 bg-destructive/5 p-2.5">
              <input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Rejection reason (required)"
                className="block h-11 w-full rounded-xl border border-border bg-card px-3 text-[13px] text-foreground placeholder:text-muted-foreground/70 focus:border-destructive focus:outline-none focus:ring-2 focus:ring-destructive/30"
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setRejectOpen(false);
                    setRejectReason('');
                  }}
                  disabled={busy}
                  className="pwa-tap h-11 rounded-xl bg-muted text-[13px] font-medium text-foreground active:bg-muted/70"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={rejectReturn}
                  disabled={busy || !rejectReason.trim()}
                  className="pwa-tap h-11 rounded-xl bg-destructive text-[13px] font-semibold text-destructive-foreground shadow-sm active:opacity-90 disabled:opacity-40"
                >
                  Reject
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
